/**
 * @ordo/screenshot-bridge
 *
 * postMessage handshake for the Workspaces annotator (@ordo/ui
 * screen-annotator). Mount this once in each embeddable app's client
 * entry (after the React root mounts is fine — the handler is just a
 * window listener). The annotator on previews.ordo.com posts:
 *
 *   { type: 'ordo.requestScreenshot', requestId, viewport }
 *
 * The bridge runs html2canvas inside the embedded app's own origin
 * (so it has DOM access the parent doesn't) and responds:
 *
 *   { type: 'ordo.screenshotReady', requestId, dataUrl }
 *
 * Security: by default only responds when framed from an .ordo.com
 * origin AND when the request looks well-formed. No-op when the page
 * is at top-level (not framed), so production users on factory.ordo.com
 * etc. never load html2canvas.
 *
 * Usage:
 *   import { mountScreenshotBridge } from '@ordo/screenshot-bridge'
 *   mountScreenshotBridge()
 */

export interface MountScreenshotBridgeOptions {
  /** Origin regex the parent must match before we honor a request.
   *  Defaults to any `https://*.ordo.com` (including previews.ordo.com).
   *  Override for local dev (e.g. `^http://localhost:\\d+`) or in tests. */
  allowedParentOrigin?: RegExp
  /** Override the screenshot implementation for tests. The default lazy-
   *  imports `html2canvas` so we don't pay for it on top-level loads. */
  screenshotImpl?: (target: HTMLElement) => Promise<HTMLCanvasElement>
  /** Mount even when not framed. Off by default — production apps don't
   *  benefit from running the bridge at top-level. */
  forceMount?: boolean
}

const DEFAULT_ALLOWED_PARENT = /^https:\/\/[a-z0-9-]+(\.[a-z0-9-]+)*\.ordo\.com$/i

let mounted = false

/**
 * Install the postMessage listener. Idempotent — calling twice in the
 * same window is a no-op. Returns an unmount function for tests.
 */
export function mountScreenshotBridge(opts: MountScreenshotBridgeOptions = {}): () => void {
  if (typeof window === 'undefined') {
    return () => {}
  }
  // Not framed → don't ship the listener (saves html2canvas load).
  if (window.top === window && !opts.forceMount) {
    return () => {}
  }
  if (mounted) {
    return () => {}
  }
  mounted = true

  const allowedParent = opts.allowedParentOrigin ?? DEFAULT_ALLOWED_PARENT
  const handler = async (event: MessageEvent) => {
    if (!allowedParent.test(event.origin)) {
      return
    }
    const data = event.data as { type?: unknown; requestId?: unknown }
    if (data?.type !== 'ordo.requestScreenshot' || typeof data.requestId !== 'string') {
      return
    }
    const requestId = data.requestId
    try {
      const canvas = await (opts.screenshotImpl ?? defaultScreenshot)(document.body)
      const dataUrl = canvas.toDataURL('image/png')
      ;(event.source as Window | null)?.postMessage(
        { type: 'ordo.screenshotReady', requestId, dataUrl },
        { targetOrigin: event.origin },
      )
    } catch (err) {
      ;(event.source as Window | null)?.postMessage(
        {
          type: 'ordo.screenshotReady',
          requestId,
          error: err instanceof Error ? err.message : String(err),
        },
        { targetOrigin: event.origin },
      )
    }
  }
  window.addEventListener('message', handler)
  return () => {
    window.removeEventListener('message', handler)
    mounted = false
  }
}

type Html2Canvas = (
  element: HTMLElement,
  options?: { backgroundColor?: string | null; logging?: boolean; useCORS?: boolean },
) => Promise<HTMLCanvasElement>

async function defaultScreenshot(target: HTMLElement): Promise<HTMLCanvasElement> {
  // `html2canvas-pro` (maintained fork) over the stock `html2canvas@1.4.1`,
  // which throws `unknown color function "oklab"/"oklch"` on any page using
  // modern CSS color spaces — i.e. every Tailwind v4 app (its design tokens
  // compile to `oklch(...)`). The fork parses oklch/oklab/lab/color() and is
  // otherwise API-compatible.
  const mod = (await import('html2canvas-pro')) as { default?: Html2Canvas } & Html2Canvas
  const html2canvas: Html2Canvas = mod.default ?? mod
  return html2canvas(target, {
    backgroundColor: null,
    logging: false,
    useCORS: true,
  })
}
